<template>
    <div class="main">
        <div class="body-content">
            <h1 class="title">Captured Data</h1>
            <div v-if="isLoading">Loading records...</div>
            <div v-else-if="error">Error: {{ error }}</div>

            <div v-else>
                <div v-if="records.length === 0">No records found.</div>

                <div v-else class="table-container">
                    <table class="data-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Movement</th>
                                <th>Container No</th>
                                <th>ISO Code</th>
                                <th>Max Gross (kg)</th>
                                <th>Verified</th>
                                <th>Location</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="rec in records" :key="rec.id">
                                <td>{{ formatDate(rec.create_date) }}</td>
                                <td>{{ rec.movement }}</td>
                                <td>{{ rec.container_number }}</td>
                                <td>{{ rec.iso_code }}</td>
                                <td>{{ rec.max_gross_weight_kg }}</td>
                                <td :class="rec.verified_status == 0 ? 'status-not-verified' : 'status-verified'">{{ rec.verified_status == 0 ? 'Not Verified' : 'Verified' }}</td>
                                <td>{{ rec.location }}</td>
                                <td>
                                    <button class="small-btn" @click="showDetails(rec)">Details</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Details modal -->
                <div v-if="selectedRecord" class="modal-overlay confirm-modal">
                    <div class="modal-content large" role="dialog" aria-modal="true">
                        <div class="modal-header">
                            <h3 style="margin:0;">Record Details</h3>
                            <button class="close-btn" @click="closeDetails">&times;</button>
                        </div>
                        <div class="modal-body modal-large-body">
                            <div class="left" ref="imgContainer"
                                 @mousemove="onMagnify"
                                 @mouseenter="onMagnifyEnter"
                                 @mouseleave="onMagnifyLeave">
                                <img v-if="selectedRecord.image_base64" :src="imageSrc(selectedRecord.image_base64)" alt="Captured Image" ref="detailImage" />
                                <div v-show="magnifier.visible" class="magnifier" :style="magnifierStyle"></div>
                            </div>
                            <div class="right">
                                <table class="details-table">
                                    <tbody>
                                        <tr><th>Container No</th><td>{{ selectedRecord.container_number }}</td></tr>
                                        <tr><th>ISO Code</th><td>{{ selectedRecord.iso_code }}</td></tr>
                                        <tr><th>Tare (kg)</th><td>{{ selectedRecord.tare_weight_kg }}</td></tr>
                                        <tr><th>Max Gross (kg)</th><td>{{ selectedRecord.max_gross_weight_kg }}</td></tr>
                                        <tr><th>Net Wt(kg)</th><td>{{ selectedRecord.net_weight_kg }}</td></tr>
                                        <tr><th>Location</th><td>{{ selectedRecord.location }}</td></tr>
                                        <tr><th>Movement</th><td>{{ selectedRecord.movement }}</td></tr>
                                        <tr v-for="(val, key) in extraFields(selectedRecord)" :key="key">
                                            <th>{{ key }}</th>
                                            <td>{{ val }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button class="cancel-button" @click="closeDetails">Close</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { hasPermission } from '../utils/permissions';

export default {
    data() {
        return {
            records: [],
            isLoading: false,
            error: null,
            perm: {
                canView: false,
                canEdit: false,
                canDelete: false,
            },
            selectedRecord: null,
            magnifier: {
                visible: false,
                x: 0,
                y: 0,
                size: 180,
                zoom: 2.2,
                bg: '',
                containerRect: null,
            },
        };
    },
    created() {
        this.checkPermissions();
        if (this.perm.canView) {
            this.fetchImages();
        }
    },
    methods: {
        checkPermissions() {
            const actionKey = 'get_image_captured_data';
            const canView = hasPermission(actionKey, 'view');
            const canEdit = hasPermission(actionKey, 'edit');
            const canDelete = hasPermission(actionKey, 'delete');
            this.perm = { canView, canEdit, canDelete };
        },

        formatDate(iso) {
            if (!iso && iso !== 0) return '';
            try {
                // Accept timestamps or ISO strings
                // treat purely numeric strings as timestamps
                let d = (typeof iso === 'number' || /^\d+$/.test(String(iso))) ? new Date(Number(iso)) : new Date(String(iso));
                if (isNaN(d.getTime())) return String(iso);
                const pad = (n) => (n < 10 ? '0' + n : String(n));
                const dd = pad(d.getDate());
                const mm = pad(d.getMonth() + 1);
                const yyyy = d.getFullYear();
                const hh = pad(d.getHours());
                const min = pad(d.getMinutes());
                return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
            } catch (e) {
                return String(iso);
            }
        },

        async fetchImages() {
            this.isLoading = true;
            this.error = null;
            const toast = useToast();
            const token = localStorage.getItem('access_token');
            if (!token) {
                this.error = 'No access token';
                this.isLoading = false;
                return;
            }

            try {
                const config = { headers: { accept: 'application/json', Authorization: `Bearer ${token}` } };
                // endpoint name based on user request — adjust if your backend uses a different path
                const res = await axios.get('/get_image_captured_data/', config);
                //console.log('Fetched images', res);
                if (res.data[0] !== 1) {
                    this.error = res.data[1] || 'Failed to fetch images';
                    toast.error(this.error);
                    this.records = [];
                    return;
                } else {
                    toast.success('Captured images fetched successfully');
                    this.records = res.data[2];
                }
            } catch (err) {
                console.error('Error fetching images', err);
                this.error = 'Error fetching images';
                toast.error('Error fetching captured images');
            } finally {
                this.isLoading = false;
            }
        },
        async showDetails(rec) {
            if (!rec || !rec.id) return;
            const toast = useToast();
            const token = localStorage.getItem('access_token');
            if (!token) {
                toast.error('No access token');
                return;
            }

            try {
                const config = { headers: { accept: 'application/json', Authorization: `Bearer ${token}` } };
                const res = await axios.get(`/get_image_captured_data/${rec.id}`, config);
                //console.log('Fetched record details', res);
                if (res.data[0] !== 1) {
                    toast.error(res.data[1] || 'Failed to fetch record details');
                    return;
                }
                this.selectedRecord = res.data[2];
            } catch (err) {
                console.error('Error fetching record details', err);
                toast.error('Error fetching record details');
            }
        },
        closeDetails() {
            this.selectedRecord = null;
        },
        formatJSON(obj) {
            try {
                return JSON.stringify(obj, null, 2);
            } catch (e) {
                return String(obj);
            }
        },
        imageSrc(val) {
            if (!val) return '';
            const s = String(val).trim();
            if (s.startsWith('data:')) return s;
            if (s.startsWith('http://') || s.startsWith('https://')) return s;
            return `data:image/jpeg;base64,${s}`;
        },
        extraFields(obj) {
            // return other fields not shown explicitly
            if (!obj || typeof obj !== 'object') return {};
            const shown = new Set(['id','movement','container_number','iso_code','max_gross_weight_kg','verified_status','location','image_base64','tare_weight_kg','net_weight_kg','create_date','user_id']);
            const out = {};
            Object.keys(obj).forEach(k => {
                if (!shown.has(k)) out[k] = obj[k];
            });
            return out;
        },
        // computed style for magnifier
        getMagnifierStyle() {
            const m = this.magnifier;
            if (!m.containerRect) return {};
            const size = m.size;
            const zoom = m.zoom;
            const cx = m.containerRect.width;
            const cy = m.containerRect.height;
            // background size (zoomed image)
            const bgW = cx * zoom;
            const bgH = cy * zoom;
            // background position: center the magnified area under the cursor
            const bx = -((m.x / cx) * bgW - size / 2);
            const by = -((m.y / cy) * bgH - size / 2);
            return {
                width: size + 'px',
                height: size + 'px',
                left: (m.x - size / 2) + 'px',
                top: (m.y - size / 2) + 'px',
                backgroundImage: `url(${m.bg})`,
                backgroundSize: `${Math.round(bgW)}px ${Math.round(bgH)}px`,
                backgroundPosition: `${Math.round(bx)}px ${Math.round(by)}px`,
            };
        },
        onMagnifyEnter() {
            if (!this.$refs.detailImage) return;
            this.magnifier.visible = true;
            // set bg to the image src
            const img = this.$refs.detailImage;
            this.magnifier.bg = img.src || '';
            // measure sizes
            const rect = this.$refs.imgContainer.getBoundingClientRect();
            this.magnifier.containerRect = rect;
        },
        onMagnifyLeave() {
            this.magnifier.visible = false;
        },
        onMagnify(e) {
            if (!this.magnifier.visible || !this.$refs.imgContainer) return;
            const rect = this.$refs.imgContainer.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            this.magnifier.x = x;
            this.magnifier.y = y;
            this.magnifier.containerRect = rect;
        },
    },
    computed: {
        magnifierStyle() {
            return this.getMagnifierStyle();
        }
    }
};
</script>

<style scoped>
.title {
    font-size: 20px;
    margin-bottom: 12px;
}

.controls {
    margin-bottom: 12px;
}

.add-button {
    padding: 8px 12px;
    border-radius: 4px;
    background: #007bff;
    color: #fff;
    border: none;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
}

.data-table th,
.data-table td {
    border: 1px solid #e5e5e5;
    padding: 8px;
    text-align: left;
    font-size: 13px;
}

.data-table th {
    background: #f7f7f7;
    font-weight: 700;
}

.small-btn {
    padding: 6px 8px;
    font-size: 12px;
    border-radius: 4px;
    border: none;
    background: #17a2b8;
    color: #fff;
}

.table-container {
    overflow-x: auto;
}

.status-verified {
    background-color: #d4edda;
    color: #155724;
    font-weight: 600;
}

.status-not-verified {
    background-color: #fff3cd;
    color: #856404;
    font-weight: 600;
}

.modal-body pre {
    max-height: 50vh;
    overflow: auto;
}

/* Modal styles (make details modal visible and centered) */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
}
.modal-content.small {
    width: 720px;
    max-width: 95%;
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: 90vh;
}
.modal-content.large {
    width: 1100px;
    max-width: 98%;
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: 94vh;
}
.modal-header { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; border-bottom:1px solid #eee; }
.modal-footer { padding:12px 16px; border-top:1px solid #eee; text-align:right; }
.close-btn { background: transparent; border: none; font-size: 20px; cursor: pointer; }
.cancel-button { padding: 8px 12px; border-radius: 4px; background:#f1f1f1; border:1px solid #ddd; }
.details-table { width: 100%; border-collapse: collapse; }
.details-table th { text-align: left; padding: 8px; width: 30%; vertical-align: top; color:#333; }
.details-table td { padding: 8px; vertical-align: top; color:#222; }
.modal-large-body { display:flex; gap:16px; padding:12px; }
.modal-large-body .left { flex: 0 0 45%; padding: 8px; }
.modal-large-body .right { flex: 1 1 55%; padding: 8px; overflow:auto; max-height: calc(94vh - 140px); }
.modal-large-body .left { overflow: hidden; }
.modal-large-body img { width:100%; height:auto; max-height: calc(94vh - 145px); object-fit: contain; border-radius:6px; display:block; transition: transform 240ms ease; }
.magnifier {
    position: absolute;
    pointer-events: none;
    border-radius: 0;
    box-shadow: 0 4px 12px rgba(0,0,0,0.35);
    border: 3px solid rgba(255,255,255,0.9);
    background-repeat: no-repeat;
    background-position: center;
}
</style>
